import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon";

/** Reusable empty-state primitive — used wherever a list/page is
 *  intentionally empty (Home with no meetings, filtered-empty, Frames
 *  with no captures, Settings/Integrations with nothing connected).
 *  Centers vertically; caps content width at 480 px.
 */
export function EmptyState({
  icon,
  heading,
  subline,
  steps,
  cta,
}: {
  icon: IconName | string;
  heading: ReactNode;
  subline?: ReactNode;
  /** Optional 1–3 step "next step" list for onboarding-like empty states. */
  steps?: ReadonlyArray<ReactNode>;
  cta?: { label: string; href?: string; onClick?: () => void };
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        padding: 32,
        maxWidth: 480,
        margin: "32px auto",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: "var(--paper-2)",
          border: "1px solid var(--rule)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ink-3)",
        }}
      >
        <Icon name={icon} size={22} color="var(--ink-3)" />
      </div>
      <h2
        className="serif"
        style={{
          margin: 0,
          fontSize: 26,
          fontWeight: 400,
          lineHeight: 1.15,
          color: "var(--ink)",
        }}
      >
        {heading}
      </h2>
      {subline && (
        <p
          className="dim"
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          {subline}
        </p>
      )}
      {steps && steps.length > 0 && (
        <ol
          style={{
            margin: "8px 0 0",
            padding: "0 0 0 20px",
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            fontSize: 13,
            lineHeight: 1.5,
            color: "var(--ink-2)",
          }}
        >
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}
      {cta &&
        (cta.href ? (
          <a
            className="btn primary"
            href={cta.href}
            style={{ marginTop: 8, textDecoration: "none" }}
          >
            {cta.label}
          </a>
        ) : (
          <button
            type="button"
            className="btn primary"
            onClick={cta.onClick}
            style={{ marginTop: 8 }}
          >
            {cta.label}
          </button>
        ))}
    </div>
  );
}
